/*
 * Purpose: Grouped page actions — enforces one primary CTA per cluster
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  assertSinglePrimaryAction,
  resolveActionTierProps,
  type ActionTier,
} from '@/lib/actionHierarchy';
import { cn } from '@/lib/utils';

export type ActionButtonItem = {
  tier: ActionTier;
  label: React.ReactNode;
  href?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit';
};

export interface ActionButtonGroupProps {
  actions: ActionButtonItem[];
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  /** Dev warning context label */
  context?: string;
}

export function ActionButtonGroup({
  actions,
  size = 'sm',
  className,
  context = 'ActionButtonGroup',
}: ActionButtonGroupProps) {
  React.useEffect(() => {
    assertSinglePrimaryAction(
      actions.map((a) => a.tier),
      context
    );
  }, [actions, context]);

  return (
    <div className={cn('flex flex-wrap items-center gap-2 shrink-0', className)}>
      {actions.map((action, index) => {
        const tierProps = resolveActionTierProps(action.tier);
        const content = (
          <>
            {action.icon}
            {action.label}
          </>
        );

        if (action.href) {
          return (
            <Button
              key={`${action.tier}-${index}`}
              asChild
              size={size}
              variant={tierProps.variant}
              className={cn(tierProps.className, action.className)}
              disabled={action.disabled}
            >
              <Link to={action.href}>{content}</Link>
            </Button>
          );
        }

        return (
          <Button
            key={`${action.tier}-${index}`}
            type={action.type ?? 'button'}
            size={size}
            variant={tierProps.variant}
            className={cn(tierProps.className, action.className)}
            disabled={action.disabled}
            onClick={action.onClick}
          >
            {content}
          </Button>
        );
      })}
    </div>
  );
}
